(ns ui.view.transaction-process.fund-config
  (:require
   [common.localization :refer [tr]]
   [common.transaction-process.notifications :as tp-notifications]
   [common.transaction-process.resolver :as resolver]
   [common.transaction-process.templates.registry :as template-registry]
   [reagent.core :as r]
   [ui.components.toggle.view :as toggle]
   [ui.editors.data-input :as data-input]
   [ui.view.transaction-process.util :as tp-util]))

(defn- team-members-fields
  [{:keys [template template-members on-change]}]
  (let [roles (:template/team-roles template)]
    (when (seq roles)
      [:div {:class "flex flex-col gap-3"}
       [:span {:class "text-sm m-0 font-semibold text-gray-700"} (tr :transaction-process/default-team-members)]
       (for [role roles]
         (let [role-id (:team-role/id role)]
           ^{:key role-id}
           [:div
            [:label {:class "text-xs m-0 text-gray-500 mb-1 block"}
             (:team-role/label role)]
            [data-input/view
             {:editor-type        :editor.type/search
              :data-type          :data.type/ref-multi
              :field/entity-types [:entity.type/account]
              :field/os-filter-terms-fn (fn [_] {"extras.active-user" true})
              :value              (get template-members role-id [])
              :on-change          (fn [_ids options]
                                    (on-change (assoc template-members
                                                      role-id (or options []))))
              :full-width?        true}]]))])))

(defn- decision-participant-fields
  [{:keys [template default-participants on-change]}]
  ;; Decision slots come from the dictionary (each {:slot-kw :entry}); the
  ;; default-participants map is keyed by slot-kw (the same key the decisions card
  ;; reads and the modal sends as :slot-attr).
  (let [decision-slots (resolver/slots-by-category (:template/dictionary template) {} :decisions)]
    (when (seq decision-slots)
      [:div {:class "flex flex-col gap-3"}
       [:span {:class "text-sm m-0 font-semibold text-gray-700"} (tr :transaction-process/default-decision-participants)]
       [:p {:class "text-xs m-0 text-gray-500 -mt-1"} (tr :transaction-process/default-participants-info)]
       (for [{:keys [slot-kw entry]} decision-slots]
         ^{:key slot-kw}
         [:div
          [:label {:class "text-xs m-0 text-gray-500 mb-1 block"}
           (:slot/label entry)]
          [data-input/view
           {:editor-type        :editor.type/search
            :data-type          :data.type/ref-multi
            :field/entity-types [:entity.type/account]
            :field/os-filter-terms-fn (fn [_] {"extras.active-user" true})
            :value              (get default-participants slot-kw [])
            :on-change          (fn [_ids options]
                                  (on-change (assoc default-participants
                                                    slot-kw (or options []))))
            :full-width?        true}]])])))

(defn- trigger-label
  [rule template]
  (case (:notification-type rule)
    :creation (tr :transaction-process/notification-type-creation)
    :stage-transition
    (let [from-label (tp-notifications/stage-label template (:from-stage rule))
          to-label   (tp-notifications/stage-label template (:to-stage rule))]
      (if (= :backward (:direction rule))
        (tr :transaction-process/notification-type-backward [to-label])
        (tr :transaction-process/notification-type-forward [from-label to-label])))
    [tp-util/render-error {:context "Unknown notification rule :notification-type"
                           :value (:notification-type rule)}]))

(defn- recipient-labels
  [recipients template]
  (let [roles-by-id (->> (:template/team-roles template)
                         (map (fn [r] [(:team-role/id r) (:team-role/label r)]))
                         (into {}))
        sorted-ids (->> recipients
                        (sort-by #(or (get roles-by-id %) (str %))))]
    (into [:<>]
          (->> sorted-ids
               (map (fn [id]
                      (if-let [label (get roles-by-id id)]
                        label
                        [tp-util/render-error {:context "Unknown notification recipient role id"
                                               :value id}])))
               (interpose ", ")))))

(defn- notification-row
  [{:keys [rule template]}]
  (let [expanded? (r/atom false)]
    (fn [{:keys [rule template]}]
      [:div {:class "border border-gray-100 rounded p-3"}
       [:div {:class "flex items-start justify-between cursor-pointer"
              :on-click #(swap! expanded? not)}
        [:div {:class "flex flex-col gap-1 flex-1 min-w-0"}
         [:span {:class "text-xs m-0 font-medium text-gray-900"}
          (trigger-label rule template)]
         [:span {:class "text-xs m-0 text-gray-500"}
          (recipient-labels (:recipients rule) template)]]
        [:i {:class (str "fal fa-chevron-down text-gray-600 ml-2 text-sm transition-transform "
                         (when @expanded? "rotate-180"))}]]
       (when @expanded?
         [:div {:class "mt-2 pt-2 border-t border-gray-100 flex flex-col gap-2"}
          [:div
           [:span {:class "text-xs m-0 font-medium text-gray-500"}
            (tr :transaction-process/notification-subject)]
           [:p {:class "text-sm m-0 text-gray-800 mt-0.5 font-medium"}
            (:subject rule)]]
          [:div
           [:span {:class "text-xs m-0 font-medium text-gray-500"}
            (tr :transaction-process/notification-body)]
           [:p {:class "text-sm m-0 text-gray-700 mt-0.5 whitespace-pre-wrap"}
            (:body rule)]]])])))

(defn- notification-rules-section
  [{:keys [template]}]
  (let [rules (:template/notifications template)]
    (when (seq rules)
      [:div {:class "flex flex-col gap-3"}
       [:div {:class "flex flex-col gap-1"}
        [:span {:class "text-sm m-0 font-semibold text-gray-700"}
         (tr :transaction-process/notifications)]
        [:p {:class "text-xs m-0 text-gray-500 -mt-0.5"}
         (tr :transaction-process/notifications-info)]]
       [:div {:class "flex flex-col gap-2"}
        (for [rule rules]
          ^{:key (:rule-id rule)}
          [notification-row {:rule rule :template template}])]])))

(defn- template-card
  [{:keys [template-kw enabled? on-toggle
           template-members on-team-change
           default-participants on-participants-change]}]
  (let [template (template-registry/get-template template-kw)]
    [:div {:class "border border-gray-200 rounded-lg"}
     [:div {:class "p-4 flex items-center justify-between"}
      [:span {:class "text-sm m-0 font-medium text-gray-900"}
       (:template/name template)]
      [toggle/toggle
       {:checked?  enabled?
        :on-change on-toggle}]]
     (when enabled?
       [:div {:class "px-4 pb-4 flex flex-col gap-5 border-t border-gray-100 pt-4"}
        [team-members-fields
         {:template         template
          :template-members template-members
          :on-change        on-team-change}]
        [decision-participant-fields
         {:template             template
          :default-participants default-participants
          :on-change            on-participants-change}]
        [notification-rules-section
         {:template template}]])]))

(defn view
  [{:keys [value on-change]}]
  (let [config (or value {})
        available-templates (or (:tp-config/available-templates config) [])
        default-team-members (or (:tp-config/default-team-members config) {})
        default-decision-participants (or (:tp-config/default-decision-participants config) {})
        entries-by-kw (->> available-templates
                           (map (fn [e] [(:template-kw e) e]))
                           (into {}))
        all-template-kws (keys template-registry/templates)]
    [:div {:class "flex flex-col gap-4 mt-4"}
     [:h3 {:class "text-base m-0 font-semibold text-gray-900"}
      (tr :transaction-process/transaction-process-templates)]
     (for [kw all-template-kws]
       (let [entry (get entries-by-kw kw {:template-kw kw :enabled? false})
             enabled? (:enabled? entry)]
         ^{:key kw}
         [template-card
          {:template-kw          kw
           :enabled?             enabled?
           :on-toggle            (fn [new-enabled?]
                                   (let [others (vec (remove #(= (:template-kw %) kw) available-templates))]
                                     (on-change (assoc config :tp-config/available-templates
                                                       (if new-enabled?
                                                         (conj others {:template-kw kw :enabled? true})
                                                         others)))))
           :template-members     (get default-team-members kw {})
           :on-team-change       (fn [updated-members]
                                   (on-change (assoc config :tp-config/default-team-members
                                                     (assoc default-team-members kw updated-members))))
           :default-participants (get default-decision-participants kw {})
           :on-participants-change (fn [updated-participants]
                                     (on-change (assoc config :tp-config/default-decision-participants
                                                       (assoc default-decision-participants kw updated-participants))))}]))]))
